import React, { useState, useEffect, useContext, createContext, useMemo } from 'react';
import './App.css';
import Registration from './pages/Registration.js';
import { BrowserRouter as Router, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import LoginUserDelete from './pages/LoginUserDelete';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Layout from './pages/shared/layout/Layout';
import LayoutBase from './pages/shared/layout/LayoutBase';
import PointOfInterest from './pages/PointOfInterest';
import Trails from './pages/Trails';
import TrailsDetail from './pages/trails/TrailsDetail';
import Stations from './pages/Stations';
import StationDetail from './pages/stations/StationDetail';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import PrivateRoute from './components/routes/PrivateRoute';
import StationReservation from './pages/stations/StationReservation';
import StationRent from './pages/stations/StationRent';
import StationReports from './pages/stations/StationReports';
import StationBikes from './pages/stations/StationBikes';
import StationBikesDetails from './pages/stations/stationEbikes/StationBikesDetails';
import UsersAdminList from './pages/admin/UsersAdminList';
import UsersAdminDetail from './pages/admin/UsersAdminDetail';
import { findUserById } from './models/actions/Users';
import UserAdminDashboard from './pages/admin/UserAdminDashboard';
import UserDeletionRequest from './pages/admin/UserDeletionRequest';
import CompleteUserDelete from './pages/CompleteUserDelete';
import PaymentDashboard from './pages/payment-section/PaymentDashboard.js';
import AdminStations from './pages/admin/stations/AdminStations.js';
import AdminStationBikes from './pages/admin/stations/AdminStationBikes.js';
import AdminStationBikesDetails from './pages/admin/stations/stationEbikes/AdminStationBikesDetails.js';
import AdminStationDetail from './pages/admin/stations/AdminStationDetail.js';

const UserSessionContext = createContext({ accessToken: null, userId: null });
const App = () => {
  const [userType, setUserType] = useState(null);
  const [authData, setAuthData] = useState({ accessToken: null, userId: null });
  const history = useHistory();

  const userMemoObj = useMemo(() => ({ authData, setAuthData }), [authData]);

  const checkTypeOfUser = async (userID) => {
    if (userID) {
      const response = await findUserById({ id_user: userID }, authData.accessToken);
      if (response) {
        if (response.type == 'ADMIN') {
          setUserType('ADMIN');
        } else {
          setUserType(null);
        }
      }
    }
  };
  const userContext = useContext(UserSessionContext);

  useEffect(() => {
    checkTypeOfUser(userContext?.userId);
  }, []);

  return (
    <UserSessionContext.Provider value={userMemoObj}>
      <Router>
        <Switch>
          <Route
            exact
            path="/login"
          >
            <LayoutBase>
              <Login setUserType={setUserType} />
            </LayoutBase>
          </Route>
          <Route
            exact
            path="/users-delete/login"
          >
            <LayoutBase>
              <LoginUserDelete setUserType={null} />
            </LayoutBase>
          </Route>
          <Route
            exact
            path="/registration"
          >
            <LayoutBase>
              <Registration />
            </LayoutBase>
          </Route>
          <Route
            exact
            path="/privacy-policy"
          >
            <PrivacyPolicy />
          </Route>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/users-delete/complete-request"
          >
            <LayoutBase>
              <CompleteUserDelete setUserType={null} />
            </LayoutBase>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/"
          >
            <Layout>
              <Dashboard />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/point-of-interest"
          >
            <Layout>
              <PointOfInterest />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/stations"
          >
            <Layout>
              <Stations />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/stations/edit/:stationId"
          >
            <Layout>
              <StationDetail />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/stations/edit/:stationId/ebikes"
          >
            <Layout>
              <StationBikes />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/stations/edit/:stationId/ebikes/edit/:bikeId"
          >
            <Layout>
              <StationBikesDetails />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/stations/edit/:stationId/ebikes/edit"
          >
            <Layout>
              <StationBikesDetails />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/stations/edit/:stationId/rents"
          >
            <Layout>
              <StationRent />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/stations/edit/:stationId/reservations"
          >
            <Layout>
              <StationReservation />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/stations/edit/:stationId/reports"
          >
            <Layout>
              <StationReports />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/stations/add"
          >
            <Layout>
              <StationDetail />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/trails"
          >
            <Layout>
              <Trails />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/trails/edit/:trailId"
          >
            <Layout>
              <TrailsDetail />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/trails/edit"
          >
            <Layout>
              <TrailsDetail />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/payment-account"
          >
            <Layout>
              <PaymentDashboard />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/admin"
          >
            <Layout userAdmin>
              <UserAdminDashboard />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/admin/stations"
          >
            <Layout userAdmin>
              <AdminStations />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/admin/stations/edit/:stationId"
          >
            <Layout userAdmin>
              <AdminStationDetail />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/admin/stations/add"
          >
            <Layout userAdmin>
              <AdminStationDetail />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/admin/stations/edit/:stationId/ebikes"
          >
            <Layout>
              <AdminStationBikes />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/admin/stations/edit/:stationId/ebikes/edit/:bikeId"
          >
            <Layout>
              <AdminStationBikesDetails />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/admin/stations/edit/:stationId/ebikes/edit"
          >
            <Layout>
              <AdminStationBikesDetails />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/admin/users"
          >
            <Layout userAdmin>
              <UsersAdminList />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/admin/users/edit"
          >
            <Layout userAdmin>
              <UsersAdminDetail />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/admin/users/edit/:userID"
          >
            <Layout userAdmin>
              <UsersAdminDetail />
            </Layout>
          </PrivateRoute>
          <PrivateRoute
            checkTypeOfUser={checkTypeOfUser}
            userType={userType}
            exact
            path="/admin/delete-requests"
          >
            <Layout userAdmin>
              <UserDeletionRequest />
            </Layout>
          </PrivateRoute>
        </Switch>
      </Router>
    </UserSessionContext.Provider>
  );
};

export { UserSessionContext };
export default App;
